/**
 * SourceApplicationService
 * Service for source generator application API interactions
 *
 * @version 2025.1.0
 */

import apiClient from './apiClient';
import {
  ApplicationLevel,
  SourceApplicationData,
  SourcesForTargetResponse,
  CriticalSourceExposureAlerts,
} from '../types/sourceApplication';

// ============================================================================
// TYPES
// ============================================================================

interface ApplyRequest {
  sourceGeneratorId: number;
  exposureLevel?: string;
  startDate?: string | null;
  endDate?: string | null;
  observations?: string | null;
}

interface ApplyMultipleRequest {
  sourceGeneratorIds: number[];
  exposureLevel?: string;
  startDate?: string | null;
  endDate?: string | null;
  observations?: string | null;
}

/**
 * API Response wrapper type
 */
interface ApiResponse<T> {
  data?: T;
}

// ============================================================================
// HELPER
// ============================================================================

/**
 * Unwrap data from API response safely
 */
function unwrapData<T>(response: ApiResponse<T>): T {
  if (response.data !== undefined) {
    return response.data;
  }
  // If no data field, return the response itself cast as T
  return response as unknown as T;
}

// ============================================================================
// SERVICE
// ============================================================================

export const sourceApplicationService = {
  /**
   * Get applied and available sources for a target
   */
  async getForTarget(
    level: ApplicationLevel,
    targetId: number
  ): Promise<SourcesForTargetResponse> {
    const response = await apiClient.get<SourcesForTargetResponse>(
      `/source-applications/${level}/${targetId}`
    );
    return unwrapData(response);
  },

  /**
   * Apply a source generator to a target
   */
  async applyToTarget(
    level: ApplicationLevel,
    targetId: number,
    data: ApplyRequest
  ): Promise<void> {
    await apiClient.post(`/source-applications/${level}/${targetId}/apply`, data);
  },

  /**
   * Apply multiple source generators to a target
   */
  async applyMultiple(
    level: ApplicationLevel,
    targetId: number,
    sourceGeneratorIds: number[],
    data?: SourceApplicationData
  ): Promise<void> {
    const payload: ApplyMultipleRequest = {
      sourceGeneratorIds,
      ...data,
    };
    await apiClient.post(`/source-applications/${level}/${targetId}/apply`, payload);
  },

  /**
   * Remove a source generator application from a target
   */
  async removeFromTarget(
    level: ApplicationLevel,
    targetId: number,
    sourceGeneratorId: number
  ): Promise<void> {
    await apiClient.delete(
      `/source-applications/${level}/${targetId}/sources/${sourceGeneratorId}`
    );
  },

  /**
   * Remove multiple source generator applications from a target
   */
  async removeMultiple(
    level: ApplicationLevel,
    targetId: number,
    sourceGeneratorIds: number[]
  ): Promise<void> {
    // Use individual DELETE calls instead of batch endpoint for reliability
    for (const sourceGeneratorId of sourceGeneratorIds) {
      await this.removeFromTarget(level, targetId, sourceGeneratorId);
    }
  },

  /**
   * Update a source generator application for a target
   */
  async updateForTarget(
    level: ApplicationLevel,
    targetId: number,
    sourceGeneratorId: number,
    data: SourceApplicationData
  ): Promise<void> {
    await apiClient.put(
      `/source-applications/${level}/${targetId}/sources/${sourceGeneratorId}`,
      data
    );
  },

  /**
   * Get critical exposure alerts for a company
   */
  async getAlertsForCompany(companyId: number): Promise<CriticalSourceExposureAlerts> {
    const response = await apiClient.get<CriticalSourceExposureAlerts>(
      `/source-applications/company/${companyId}/alerts`
    );
    return unwrapData(response);
  },

  /**
   * Health check for source applications service
   */
  async healthCheck(): Promise<{ status: string }> {
    const response = await apiClient.get<{ status: string }>(
      '/source-applications/health'
    );
    return unwrapData(response);
  },
};

export default sourceApplicationService;
